import { useState } from "react";
import { Upload, X, Loader2, Stamp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { validateImageFile, generateUniqueFileName, resizeImage, getImageUrl } from "@/lib/imageUtils";

interface SelloUploadProps {
  empresaId: string;
  currentSelloUrl?: string | null;
  onSelloUpdated: (newSelloUrl: string | null) => void;
}

export function SelloUpload({ empresaId, currentSelloUrl, onSelloUpdated }: SelloUploadProps) {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState<string | null>(currentSelloUrl || null);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validation = validateImageFile(file);
    if (!validation.valid) {
      toast.error(validation.error);
      return;
    }

    try {
      setUploading(true);

      if (currentSelloUrl) {
        const oldPath = currentSelloUrl.split('/').pop();
        if (oldPath) {
          await supabase.storage.from('empresa-logos').remove([oldPath]);
        }
      }

      const optimizedBlob = await resizeImage(file, 600, 600);
      const fileName = `sello_${generateUniqueFileName(file, empresaId)}`;

      const { error: uploadError } = await supabase.storage
        .from('empresa-logos')
        .upload(fileName, optimizedBlob, {
          contentType: 'image/webp',
          upsert: false,
        });
      if (uploadError) throw uploadError;

      const selloUrl = getImageUrl(fileName);

      const { error: updateError } = await supabase
        .from('empresas')
        .update({ sello_url: fileName } as any)
        .eq('id', empresaId);
      if (updateError) throw updateError;

      setPreview(selloUrl);
      onSelloUpdated(selloUrl);
      toast.success('Sello/Firma de empresa actualizado');
    } catch (error) {
      console.error('Error uploading sello:', error);
      toast.error('Error al subir el sello');
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async () => {
    try {
      setUploading(true);
      if (currentSelloUrl) {
        const oldPath = currentSelloUrl.split('/').pop();
        if (oldPath) {
          await supabase.storage.from('empresa-logos').remove([oldPath]);
        }
      }
      const { error } = await supabase
        .from('empresas')
        .update({ sello_url: null } as any)
        .eq('id', empresaId);
      if (error) throw error;
      setPreview(null);
      onSelloUpdated(null);
      toast.success('Sello eliminado');
    } catch (error) {
      console.error('Error removing sello:', error);
      toast.error('Error al eliminar el sello');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium">Sello / Firma de la empresa</label>
        <p className="text-xs text-muted-foreground mt-1">
          Se aplicará automáticamente en contratos, partes de trabajo, informes y checklists generados.
        </p>
      </div>

      <div className="flex items-center gap-4">
        {preview ? (
          <div className="relative w-32 h-20 rounded-lg border-2 border-border overflow-hidden bg-white p-2">
            <img src={preview} alt="Sello preview" className="w-full h-full object-contain" />
            {!uploading && (
              <button
                onClick={handleRemove}
                className="absolute top-1 right-1 p-1 bg-destructive text-destructive-foreground rounded-full hover:bg-destructive/90 transition-colors"
                type="button"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
        ) : (
          <div className="w-24 h-24 rounded-lg border-2 border-dashed border-border flex items-center justify-center bg-muted/30">
            <Stamp className="h-8 w-8 text-muted-foreground" />
          </div>
        )}

        <div className="flex-1">
          <input
            type="file"
            id={`sello-upload-${empresaId}`}
            accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
            onChange={handleFileSelect}
            disabled={uploading}
            className="hidden"
          />
          <label htmlFor={`sello-upload-${empresaId}`}>
            <Button type="button" variant="outline" disabled={uploading} asChild>
              <span className="cursor-pointer">
                {uploading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Subiendo...
                  </>
                ) : (
                  <>
                    <Upload className="mr-2 h-4 w-4" />
                    {preview ? 'Cambiar Sello' : 'Subir Sello/Firma'}
                  </>
                )}
              </span>
            </Button>
          </label>
          <p className="text-xs text-muted-foreground mt-2">
            PNG, JPG, SVG o WebP. Fondo transparente recomendado. Máximo 2MB.
          </p>
        </div>
      </div>
    </div>
  );
}
